import { useTranslation } from 'react-i18next'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import AddRoundedIcon from '@mui/icons-material/AddRounded'

type AvailabilitiesEmptyStateProps = {
  onSetup: () => void
}

export default function AvailabilitiesEmptyState({ onSetup }: AvailabilitiesEmptyStateProps) {
  const { t } = useTranslation()

  return (
    <Stack spacing={3} alignItems="center" sx={{ py: 4, px: 2 }}>
      <Box
        sx={{
          textAlign: 'center',
          p: 5,
          bgcolor: 'grey.50',
          borderRadius: 3,
          border: '2px dashed',
          borderColor: 'divider',
          width: '100%',
          maxWidth: 500,
        }}
      >
        <CalendarMonthOutlinedIcon sx={{ fontSize: 56, color: 'text.secondary', mb: 1.5 }} />
        <Typography variant="h6" gutterBottom>
          {t('page.meetingLink.emptyState.title', 'No availabilities yet')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 340, mx: 'auto' }}>
          {t(
            'page.meetingLink.emptyState.description',
            'Patients can\'t book appointments until you set up when you\'re available. It takes about 2 minutes.'
          )}
        </Typography>
        <Button
          variant="contained"
          size="large"
          startIcon={<AddRoundedIcon />}
          onClick={onSetup}
          data-testid="add-availability-button"
        >
          {t('page.meetingLink.emptyState.cta', 'Set Up Your First Availability')}
        </Button>
      </Box>

      <Alert severity="info" variant="outlined" sx={{ maxWidth: 500, width: '100%' }}>
        <Typography variant="body2">
          <strong>{t('page.meetingLink.emptyState.howItWorks.title', 'How it works:')}</strong>{' '}
          {t(
            'page.meetingLink.emptyState.howItWorks.description',
            'You tell us when and how you want to meet patients. Patients see available time slots on your meeting link page and book directly.'
          )}
        </Typography>
      </Alert>

      <Box sx={{ maxWidth: 500, width: '100%' }}>
        <Typography variant="caption" color="text.secondary" gutterBottom display="block">
          {t('page.meetingLink.emptyState.patientView', 'What patients see on your link right now:')}
        </Typography>
        <Box sx={{ bgcolor: 'error.50', borderRadius: 2, p: 1.5, textAlign: 'center' }}>
          <Typography variant="body2" color="error.main" fontWeight={500}>
            {t('page.meetingLink.emptyState.noSlots', 'No available slots')}
          </Typography>
          <Typography variant="caption" color="error.light">
            {t('page.meetingLink.emptyState.noSlotsDescription', 'Patients visiting your link can\'t book anything yet')}
          </Typography>
        </Box>
      </Box>
    </Stack>
  )
}
